import React from "react";
import { MapPin, Bell, User, BarChart3 } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import PageHero from "../components/PageHero";
import BusImage from "../components/BusImage";
import FeatureCard from "../components/FeatureCard";
import { LiveTrackingCard, AttendanceCard } from "../components/StatusCards";
import { motion } from "framer-motion";

const features = [
  {
    icon: MapPin,
    title: "Live Tracking",
    description:
      "Real-time GPS tracking shows you exact bus location, speed, and ETA.",
    bullets: [
      "Live location on map",
      "ETA updates",
      "Route deviation alerts",
    ],
  },
  {
    icon: Bell,
    title: "Parent Alerts",
    description:
      "Automated alerts keep parents informed at every step of the journey.",
    bullets: [
      "Pickup & drop notifications",
      "Delay or route alerts",
      "Instant notifications",
    ],
  },
  {
    icon: User,
    title: "Attendance Monitoring",
    description:
      "Digital attendance with boarding and drop updates in real-time.",
    bullets: [
      "Student-wise attendance",
      "On-time and not boarded insights",
      "History and reporting",
    ],
  },
  {
    icon: BarChart3,
    title: "School Admin Dashboard",
    description:
      "A centralized dashboard for schools to manage and monitor operations.",
    bullets: [
      "Live fleet overview",
      "Route & driver performance",
      "Reports & analytics",
    ],
  },
];

const AlertCard = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 0.5 }}
    className="bg-white rounded-2xl shadow-[0_18px_45px_-25px_rgba(15,27,45,0.35)] border border-slate-100 p-4 w-[230px]"
    data-testid="tech-alert-card"
  >
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-blue-50 text-[#2563EB] flex items-center justify-center shrink-0">
        <Bell size={16} />
      </div>
      <div className="text-sm">
        <div className="font-semibold text-[#0F1B2D]">Alert</div>
        <div className="text-slate-600 leading-snug mt-0.5">
          Bus has reached <span className="font-semibold">Pickle Park</span>
        </div>
        <div className="text-xs text-slate-400 mt-1">07:30 AM</div>
      </div>
    </div>
  </motion.div>
);

const Technology = () => {
  return (
    <PageWrapper testId="technology-page">
      <PageHero
        title={
          <>
            Technology That
            <br />
            Makes Transport
            <br />
            <span className="brand-blue">Transparent</span>
            <span className="text-amber-500">.</span>
          </>
        }
        description="Our smart technology suite gives schools and parents complete visibility into every ride. Live tracking, attendance visibility, route monitoring, and timely alerts help everyone stay informed and worry-free."
        cta={{ label: "How It Works", to: "/services" }}
        rightContent={
          <div className="relative xl:min-h-[460px]">
            <BusImage variant="tech" />
            <div className="hidden xl:flex flex-col gap-4 absolute right-0 top-0">
              <LiveTrackingCard />
              <AttendanceCard delay={0.5} />
            </div>
            <div className="xl:hidden mt-6 grid sm:grid-cols-2 gap-4">
              <LiveTrackingCard />
              <AlertCard />
              <AttendanceCard delay={0.7} />
            </div>
          </div>
        }
      />

      <section
        className="max-w-[1280px] mx-auto px-5 lg:px-10 -mt-2 mb-14"
        data-testid="technology-features"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <FeatureCard
              key={f.title}
              icon={f.icon}
              title={f.title}
              description={f.description}
              variant="list"
              bullets={f.bullets}
              index={i}
              testId={`technology-feature-${i}`}
            />
          ))}
        </div>
      </section>
    </PageWrapper>
  );
};

export default Technology;
